import { goRequest as request } from '../utils/httpClientTs';
import {
  SeekingTransferDetail,
  SeekingTransferCreate,
  SeekingTransferUpdate,
  SeekingTransferChapterUpdate,
  SeekingTransferChapterLookup,
  SeekingTransferChapterLookupResponse,
  PaginatedSeekingTransferResponse,
  SeekingTransferChapterLookupInitial,
  SeekingTransferChapterTransfer,
  SeekingTransferListRequest,
  PauseTransferRequest,
  CreateNoteRequest,
  NoteResponse,
  GetCannedNotesResponse,
} from '../types/seekingTransfer';

export const getSeekingTransferList = async (
  params: SeekingTransferListRequest
): Promise<PaginatedSeekingTransferResponse> => {
  // Filter out empty values to avoid blank parameters in URL
  const filteredParams: Record<string, any> = {};

  if (params.rowsPerPage) filteredParams.rowsPerPage = params.rowsPerPage;
  if (params.page !== undefined) filteredParams.page = params.page;
  if (params.orderBy) filteredParams.orderBy = params.orderBy;
  if (params.order) filteredParams.order = params.order;
  if (params.search) filteredParams.search = params.search;
  if (params.spd_id) filteredParams.spd_id = params.spd_id;
  if (params.status_id) filteredParams.status_id = params.status_id;

  return request<PaginatedSeekingTransferResponse>({
    method: 'GET',
    url: '/seeking_transfer/list',
    params: filteredParams,
  });
};

export const getSeekingTransferDetail = async (
  id: string
): Promise<SeekingTransferDetail> => {
  return request<SeekingTransferDetail>({
    method: 'GET',
    url: `/seeking_transfer/${id}`,
  });
};

export const createSeekingTransfer = async (
  data: SeekingTransferCreate
): Promise<any> => {
  return request({
    method: 'POST',
    url: '/seeking_transfer/create',
    data,
  });
};

export const updateSeekingTransfer = async (
  id: string,
  data: SeekingTransferUpdate
): Promise<any> => {
  return request({
    method: 'PUT',
    url: `/seeking_transfer/${id}`,
    data,
  });
};

export const updateSeekingTransferChapter = async (
  id: string,
  chapterId: string,
  data: SeekingTransferChapterUpdate
): Promise<any> => {
  return request({
    method: 'PUT',
    url: `/seeking_transfer/${id}/chapter/${chapterId}`,
    data,
  });
};

export const addSeekingTransferChapter = async (
  id: string,
  data: { chapter_id: string }
): Promise<any> => {
  return request({
    method: 'POST',
    url: `/seeking_transfer/${id}/chapter_add`,
    data,
  });
};

export const getChapterLookup = async (
  data: SeekingTransferChapterLookup
): Promise<SeekingTransferChapterLookupResponse> => {
  // Filter out 'all' values for chapter_size and chapter_type
  const filteredParams: Record<string, any> = { ...data };
  if (filteredParams.chapter_size === 'all') {
    delete filteredParams.chapter_size;
  }
  if (filteredParams.chapter_type === 'all') {
    delete filteredParams.chapter_type;
  }

  return request<SeekingTransferChapterLookupResponse>({
    method: 'GET',
    url: '/seeking_transfer/chapter_lookup',
    params: filteredParams,
  });
};

export const getChapterLookupInitial = async (
  data: SeekingTransferChapterLookupInitial
): Promise<SeekingTransferChapterLookupResponse> => {
  // Filter out 'all' values for chapter_size and chapter_type
  const filteredParams: Record<string, any> = { ...data };
  if (filteredParams.chapter_size === 'all') {
    delete filteredParams.chapter_size;
  }
  if (filteredParams.chapter_type === 'all') {
    delete filteredParams.chapter_type;
  }

  return request<SeekingTransferChapterLookupResponse>({
    method: 'GET',
    url: '/seeking_transfer/chapter_lookup',
    params: filteredParams,
  });
};

export const transferSeekingTransferChapter = async (
  id: string,
  chapterId: string,
  data: SeekingTransferChapterTransfer
): Promise<any> => {
  return request({
    method: 'POST',
    url: `/seeking_transfer/${id}/chapter/${chapterId}/transfer`,
    data,
  });
};

export const reopenSeekingTransferChapter = async (
  id: string,
  chapterId: string
): Promise<any> => {
  return request({
    method: 'POST',
    url: `/seeking_transfer/${id}/chapter/${chapterId}/reopen`,
  });
};

export const undoSeekingTransferContact = async (
  id: string,
  chapterId: string
): Promise<any> => {
  return request({
    method: 'POST',
    url: `/seeking_transfer/${id}/chapter/${chapterId}/undo_contact`,
  });
};

export const pauseTransferRequest = async (
  id: string,
  data: PauseTransferRequest
): Promise<any> => {
  return request({
    method: 'PUT',
    url: `/seeking_transfer/${id}/pause`,
    data,
  });
};

export const unpauseTransferRequest = async (id: string): Promise<any> => {
  return request({
    method: 'PUT',
    url: `/seeking_transfer/${id}/unpause`,
  });
};

export const getCannedNotes = async (
  cannedNoteTypeId?: string
): Promise<GetCannedNotesResponse> => {
  const params: Record<string, any> = {};
  if (cannedNoteTypeId) {
    params.canned_note_type_id = cannedNoteTypeId;
  }
  return request<GetCannedNotesResponse>({
    method: 'GET',
    url: '/seeking_transfer/canned_notes',
    params,
  });
};

export const createNote = async (
  transferRequestId: string,
  data: CreateNoteRequest
): Promise<NoteResponse> => {
  return request<NoteResponse>({
    method: 'POST',
    url: `/seeking_transfer/${transferRequestId}/notes`,
    data,
  });
};

export const getChapterNotes = async (
  transferRequestId: string,
  chapterId: string
): Promise<NoteResponse[]> => {
  return request<NoteResponse[]>({
    method: 'GET',
    url: `/seeking_transfer/${transferRequestId}/chapter/${chapterId}/notes`,
  });
};
